import { Badge } from '@/components/ui/badge';
import { ContentContainer } from '@/components/ui/content-container';
import { Heading2, Heading3, Paragraph } from '@/components/ui/typography';
import { formatProfileNames, normalizeProfiles } from '../../profile-list';
import type { CaseResultWidgetProps } from '../definition';

export function GridVariant({ title, caseResults }: CaseResultWidgetProps) {
  if (caseResults.length === 0) return null;

  return (
    <ContentContainer>
      <div className="mb-8 text-left">
        <Heading2 className="mb-2">{title}</Heading2>
        <div className="flex items-center gap-2">
          <div className="h-[2px] w-4 bg-primary rounded-full" />
          <div className="h-[2px] w-20 bg-primary rounded-full" />
        </div>
      </div>

      <div className="grid gap-4 md:gap-8 md:grid-cols-2">
        {caseResults.map((caseResult, index) => {
          const attorneys = normalizeProfiles(caseResult.attorneys);
          const attorneyLabel = formatProfileNames(attorneys);
          const courts = caseResult.courts ?? [];

          return (
            <article
              key={caseResult.id || index}
              className="group/case-item flex flex-col gap-6 rounded-lg border border-border bg-card text-card-foreground p-6 transition-all hover:shadow-md hover:-translate-y-1"
            >
              <div className="flex flex-col gap-4">
                {caseResult.title && (
                  <Heading3 className="text-2xl group-hover/case-item:text-primary transition-colors">
                    {caseResult.title}
                  </Heading3>
                )}
                {caseResult.summary && (
                  <Paragraph className="text-base text-muted-foreground line-clamp-4 leading-tight">
                    {caseResult.summary}
                  </Paragraph>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {courts.map((court) => (
                    <Badge
                      key={court.id}
                      variant="outline"
                      colorScheme="secondary"
                      className="border-input"
                    >
                      {court.name}
                    </Badge>
                  ))}
                  {caseResult.practiceAreas &&
                    caseResult.practiceAreas.length > 0 &&
                    caseResult.practiceAreas.map((area) => (
                      <Badge key={area.id || area.name} variant="outline" colorScheme="primary">
                        {area.name}
                      </Badge>
                    ))}
                </div>
                {attorneyLabel && (
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground border-t border-border pt-4">
                    <span>{attorneys.length > 1 ? 'Attorneys:' : 'Attorney:'}</span>
                    <span>{attorneyLabel}</span>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </ContentContainer>
  );
}
